import clsx from "clsx";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import { ITicketAllocation } from "@/store/tradeFromScratchStore";
import type { IAllocationsPending } from "../helper/blockEditHelper";
import {
  ALLOCATIONS_KEY,
  returnCappedSplitNominal,
  returnPrimaryAllocationNominal,
} from "../helper/allocationRules";
import ContractCountInput from "./ContractCountInput";
import NominalInput from "./NominalInput";
import PortfolioSelect from "./PortfolioSelect";
import { RequiredMarker } from "./TicketRow";

// LQC-2941 / LQC-2949 - the ticket's allocations. Row 1 is the ticket's own portfolio,
// which keeps the remainder (shown read-only once there are splits), so the total never
// has to be retyped and always matches the trade. Futures split in contracts.
interface IAllocationSplits {
  // Row ids stay `${testIdPrefix}PortfolioRow` / `...PortfolioInput`, as before.
  testIdPrefix: string;
  portfolioCode: string;
  onPortfolioChange: (portfolioCode: string) => void;
  isPortfolioRequired?: boolean;
  total: number | null;
  splits: ITicketAllocation[];
  onChange: (splits: ITicketAllocation[]) => void;
  sizeKind?: "nominal" | "contracts";
  highlightedKeys?: Set<string>;
  // LQC-3029 - an edit's changed allocation fields, shown in pending orange.
  pending?: IAllocationsPending;
}

const EMPTY_SPLIT: ITicketAllocation = { portfolioCode: "", nominal: null };

export const PRIMARY_ALLOCATION_NOTE = "Adjusts to the remaining amount";

// A removed row leaves the list, so the edit says how many went.
export function returnRemovedAllocationsNote(count: number): string {
  return `${count} allocation${count === 1 ? "" : "s"} removed`;
}

// What row 1 is left with, or "" until the trade size is known.
export function returnPrimaryShareAmount(
  total: number | null,
  splits: ITicketAllocation[],
  sizeKind: "nominal" | "contracts",
): string {
  const primary = returnPrimaryAllocationNominal(total, splits);
  if (primary === null) return "";
  return sizeKind === "contracts"
    ? `${primary} lots`
    : returnSizeFormatted(primary);
}

// One grid for every row, so the amount column lines up and the last column can widen
// for row 1's note without shifting the others. Cells carry the row styling. Pending
// recolours the cell's own control (globals.css); red (missing) wins over it.
function returnCellClass(isFlagged: boolean, extra = "", isPending = false): string {
  return clsx(
    "flex h-full min-w-0 items-center border-b px-1 py-1",
    isFlagged
      ? "border-red-500/60 bg-red-500/10"
      : "border-gray-200 dark:border-gray-700",
    isPending && !isFlagged && "draftLegPending",
    extra,
  );
}

const AllocationSplits = ({
  testIdPrefix,
  portfolioCode,
  onPortfolioChange,
  isPortfolioRequired = true,
  total,
  splits,
  onChange,
  sizeKind = "nominal",
  highlightedKeys,
  pending,
}: IAllocationSplits) => {
  const isFlagged = (key: string) => !!highlightedKeys?.has(key);
  const updateSplit = (index: number, changes: Partial<ITicketAllocation>) =>
    onChange(
      splits.map((split, i) =>
        i === index ? { ...split, ...changes } : split,
      ),
    );
  // A split can never take more than the block has left.
  const commitSplitNominal = (index: number, nominal: number | null) =>
    updateSplit(index, {
      nominal: returnCappedSplitNominal(total, splits, index, nominal),
    });
  const removeSplit = (index: number) =>
    onChange(splits.filter((_, i) => i !== index));
  const hasSplits = splits.length > 0;
  const isPortfolioFlagged = isFlagged("portfolioCode");
  const isOverAllocated = isFlagged(ALLOCATIONS_KEY);
  const removedCount = pending?.removedCount ?? 0;

  return (
    <div
      data-testid={`${testIdPrefix}AllocationSplits`}
      id={`${testIdPrefix}AllocationSplits`}
      className="flex flex-col gap-1"
    >
      <div className="grid grid-cols-[minmax(88px,140px)_1fr_7rem_auto] items-stretch">
        {/* Row 1: the ticket's own portfolio. */}
        <div
          data-testid={`${testIdPrefix}PortfolioRow`}
          id={`${testIdPrefix}PortfolioRow`}
          className={returnCellClass(
            isPortfolioFlagged,
            clsx(
              "text-xs",
              isPortfolioFlagged
                ? "font-semibold text-red-600 dark:text-red-400"
                : "text-gray-600 dark:text-gray-300",
            ),
          )}
        >
          Portfolio
          <RequiredMarker
            isRequired={isPortfolioRequired}
            isHighlighted={isPortfolioFlagged}
          />
        </div>
        <div
          className={returnCellClass(
            isPortfolioFlagged,
            hasSplits ? "" : "col-span-3",
            pending?.primary.portfolio,
          )}
        >
          <div className="w-full">
            <PortfolioSelect
              testId={`${testIdPrefix}PortfolioInput`}
              portfolioCode={portfolioCode}
              onChange={onPortfolioChange}
            />
          </div>
        </div>
        {hasSplits && (
          <>
            <div
              className={returnCellClass(
                isPortfolioFlagged,
                "",
                pending?.primary.nominal,
              )}
            >
              {/* Read-only: row 1 always takes what the splits leave. */}
              <div
                data-testid={`${testIdPrefix}PrimaryAllocationShare`}
                id={`${testIdPrefix}PrimaryAllocationShare`}
                className={clsx(
                  "flex h-[28px] w-full items-center rounded-md border px-2 text-xs",
                  isOverAllocated
                    ? "border-red-600 bg-red-500/10 font-semibold text-red-700 dark:text-red-400"
                    : "border-gray-300 bg-gray-500/10 text-gray-600 dark:border-gray-600 dark:text-gray-300",
                )}
              >
                {returnPrimaryShareAmount(total, splits, sizeKind) || "-"}
              </div>
            </div>
            <div
              data-testid={`${testIdPrefix}PrimaryAllocationNote`}
              id={`${testIdPrefix}PrimaryAllocationNote`}
              className={returnCellClass(
                isPortfolioFlagged,
                clsx(
                  "whitespace-nowrap text-xs",
                  isOverAllocated
                    ? "font-semibold text-red-600 dark:text-red-400"
                    : "text-gray-500 dark:text-gray-400",
                ),
              )}
            >
              {PRIMARY_ALLOCATION_NOTE}
            </div>
          </>
        )}

        {splits.map((split, index) => {
          const rowTestId = `${testIdPrefix}AllocationSplit-${index}`;
          const baseKey = `${ALLOCATIONS_KEY}.${index}`;
          const isRowFlagged =
            isFlagged(`${baseKey}.portfolioCode`) ||
            isFlagged(`${baseKey}.nominal`);
          return (
            <div key={index} className="contents">
              <div
                data-testid={rowTestId}
                id={rowTestId}
                className={returnCellClass(
                  isRowFlagged,
                  "text-xs text-gray-600 dark:text-gray-300",
                )}
              >
                {`Allocation ${index + 2}`}
              </div>
              <div
                className={returnCellClass(
                  isRowFlagged,
                  "",
                  pending?.splits[index]?.portfolio,
                )}
              >
                <div className="w-full">
                  <PortfolioSelect
                    testId={`${rowTestId}Portfolio`}
                    portfolioCode={split.portfolioCode}
                    onChange={(code) =>
                      updateSplit(index, { portfolioCode: code })
                    }
                  />
                </div>
              </div>
              <div
                className={returnCellClass(
                  isRowFlagged,
                  "",
                  pending?.splits[index]?.nominal,
                )}
              >
                {sizeKind === "contracts" ? (
                  <ContractCountInput
                    testId={`${rowTestId}Nominal`}
                    value={split.nominal}
                    onCommit={(nominal) => commitSplitNominal(index, nominal)}
                  />
                ) : (
                  <NominalInput
                    testId={`${rowTestId}Nominal`}
                    value={split.nominal}
                    onCommit={(nominal) => commitSplitNominal(index, nominal)}
                  />
                )}
              </div>
              <div className={returnCellClass(isRowFlagged)}>
                <button
                  data-testid={`${rowTestId}Remove`}
                  id={`${rowTestId}Remove`}
                  type="button"
                  title="Remove allocation"
                  className="px-1 text-xs opacity-70 hover:opacity-100"
                  onClick={() => removeSplit(index)}
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 px-1">
        <button
          data-testid={`${testIdPrefix}AddAllocationButton`}
          id={`${testIdPrefix}AddAllocationButton`}
          type="button"
          className="text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400"
          onClick={() => onChange([...splits, EMPTY_SPLIT])}
        >
          + Add portfolio
        </button>
        {removedCount > 0 && (
          <span
            data-testid={`${testIdPrefix}RemovedAllocationsNote`}
            id={`${testIdPrefix}RemovedAllocationsNote`}
            className="draftLegPendingText text-xs"
          >
            {returnRemovedAllocationsNote(removedCount)}
          </span>
        )}
      </div>
    </div>
  );
};

export default AllocationSplits;
